import {
  type ChangeEvent,
  type KeyboardEvent,
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';
import {
  type ScheduleTimeSlot,
  type Weekday,
} from '@/features/plants/schemas/schedule-plant.schema.ts';
import {
  Credenza,
  CredenzaContent,
  CredenzaHeader,
  CredenzaTitle,
} from '@/components/ui/credenza.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Button } from '@/components/ui/button.tsx';
import { SCHEDULE_HOURS_IN_DAY } from '../constants';
import { getPowerSetpointType, POWER_SETPOINT_COLORS } from './schedule-time-slot-colors.ts';
import { cn } from '@/utils/utils.ts';

type FormState = {
  startTime: string;
  endTime: string;
  powerSetpoint: string;
};

function TimeInput({
  value,
  onChange,
  placeholder = 'HH:MM',
  id,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
}) {
  const [displayValue, setDisplayValue] = useState(value);

  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const formatted = formatTimeInput(input);

    setDisplayValue(formatted);

    if (formatted === '' || isValidTimeFormat(formatted)) {
      onChange(formatted);
    }
  };

  const handleBlur = () => {
    const normalizedTime = normalizeTimeOnBlur(displayValue);
    if (normalizedTime) {
      setDisplayValue(normalizedTime);
      onChange(normalizedTime);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (shouldAllowKeyPress(e)) {
      return;
    }
    e.preventDefault();
  };

  return (
    <div className="relative">
      <Input
        id={id}
        type="text"
        value={displayValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        maxLength={5}
      />
      <div className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-gray-400">
        24h
      </div>
    </div>
  );
}

export function ScheduleTimeSlotWindowDialog({
  isOpen,
  mode,
  onClose,
  onSave,
  onDelete,
  day,
  initialData,
  validateTimeSlotWindow,
}: {
  isOpen: boolean;
  mode?: 'add' | 'edit';
  day?: Weekday;
  initialData?: ScheduleTimeSlot;
  onClose: () => void;
  onSave: (data: { startTime: string; endTime: string; powerSetpoint: number }) => void;
  onDelete?: () => void;
  validateTimeSlotWindow?: (
    startTime: string,
    endTime: string
  ) => { isValid: boolean; error?: string };
}) {
  const [formState, setFormState] = useState<FormState>({
    startTime: convertTo24HourTime(initialData?.startTime),
    endTime: convertTo24HourTime(initialData?.endTime),
    powerSetpoint: initialData?.powerSetpoint?.toString() ?? '0',
  });

  const dialogTitle = getDialogTitle(mode, day);

  const validationError = getTimeSlotWindowValidationError(
    formState.startTime,
    formState.endTime,
    validateTimeSlotWindow
  );
  const isFormValid = !validationError;

  const handleSave = () => {
    const saveData = prepareSaveData(
      formState.startTime,
      formState.endTime,
      formState.powerSetpoint,
      validateTimeSlotWindow
    );
    if (saveData) {
      onSave(saveData);
    }
  };

  const handleClose = () => {
    resetFormState(setFormState);
    onClose();
  };

  return (
    <Credenza open={isOpen} onOpenChange={handleClose}>
      <CredenzaContent className="p-4 sm:max-w-md sm:p-6">
        <CredenzaHeader>
          <CredenzaTitle className="h-4">{dialogTitle}</CredenzaTitle>
        </CredenzaHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="start-time">Start Time</Label>
            <TimeInput
              id="start-time"
              value={formState.startTime}
              onChange={(value) => setFormState((prev) => ({ ...prev, startTime: value }))}
              placeholder="00:00"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="end-time">End Time</Label>
            <TimeInput
              id="end-time"
              value={formState.endTime}
              onChange={(value) => setFormState((prev) => ({ ...prev, endTime: value }))}
              placeholder={`${SCHEDULE_HOURS_IN_DAY}:00`}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="power-setpoint">Power Setpoint (kW)</Label>
            <Input
              id="power-setpoint"
              type="number"
              placeholder="Enter power in kilowatts"
              value={formState.powerSetpoint}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  powerSetpoint: e.target.value,
                }))
              }
              step="1"
            />
            <PowerSetpointIndicator powerSetpoint={formState.powerSetpoint} />
          </div>

          {validationError && (
            <div className="rounded bg-red-50 p-2 text-sm text-red-600">{validationError}</div>
          )}

          <div className="flex justify-between">
            {mode === 'edit' && onDelete && (
              <Button variant="destructive" onClick={onDelete}>
                Delete
              </Button>
            )}
            <div className="ml-auto flex justify-end space-x-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={!isFormValid}>
                {mode === 'add' ? 'Add' : 'Update'}
              </Button>
            </div>
          </div>
        </div>
      </CredenzaContent>
    </Credenza>
  );
}

function PowerSetpointIndicator({ powerSetpoint }: { powerSetpoint: string }) {
  const value = parseFloat(powerSetpoint);
  const effectiveValue = isNaN(value) || powerSetpoint === '' ? 0 : value;

  const type = getPowerSetpointType(effectiveValue);
  const colors = POWER_SETPOINT_COLORS[type];

  return (
    <div className="flex items-center gap-2">
      <div className={cn('h-3 w-3 rounded-sm', colors.dot)} />
      <span className={cn('text-sm font-medium', colors.text)}>{colors.label}</span>
    </div>
  );
}

function formatTimeInput(input: string): string {
  const digits = input.replace(/\D/g, '');

  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) {
    const hours = digits.slice(0, 2);
    const minutes = digits.slice(2);
    return `${hours}:${minutes}`;
  }

  const hours = digits.slice(0, 2);
  const minutes = digits.slice(2, 4);
  return `${hours}:${minutes}`;
}

function isValidTimeFormat(timeString: string): boolean {
  if (!timeString.includes(':')) return false;

  const [hours, minutes] = timeString.split(':');
  const h = parseInt(hours, 10);
  const m = parseInt(minutes, 10);

  if (Number.isNaN(h) || Number.isNaN(m)) return false;

  if (h === SCHEDULE_HOURS_IN_DAY && m === 0) return true;

  if (h > SCHEDULE_HOURS_IN_DAY) return false;

  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

function normalizeTimeOnBlur(displayValue: string): string | null {
  if (!displayValue || !displayValue.includes(':')) return null;

  const [hours, minutes] = displayValue.split(':');
  const h = parseInt(hours, 10);
  const m = parseInt(minutes, 10);

  if (Number.isNaN(h) || Number.isNaN(m)) return null;

  if (h === SCHEDULE_HOURS_IN_DAY && m === 0) {
    return `${SCHEDULE_HOURS_IN_DAY}:00`;
  }

  if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  return null;
}

function shouldAllowKeyPress(e: KeyboardEvent<HTMLInputElement>): boolean {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
  ];

  if (allowedKeys.includes(e.key)) {
    return true;
  }

  const allowedShortcuts = ['a', 'c', 'v', 'x', 'z'];
  if ((e.ctrlKey || e.metaKey) && allowedShortcuts.includes(e.key.toLowerCase())) {
    return true;
  }

  return /^[0-9:]$/.test(e.key);
}

function convertTo24HourTime(time?: string): string {
  if (!time) return '';

  if (time.includes(':') && !time.includes('AM') && !time.includes('PM')) {
    return time;
  }

  if (time.includes('AM') || time.includes('PM')) {
    const [timePart, modifier] = time.split(' ');
    const [hoursInput, minutes] = timePart.split(':');
    let hours = hoursInput;

    if (hours === '12') {
      hours = modifier === 'AM' ? '00' : '12';
    } else {
      hours =
        modifier === 'AM' ? hours.padStart(2, '0') : (parseInt(hours, 10) + 12).toString();
    }

    return `${hours}:${minutes}`;
  }

  return time;
}

function getDialogTitle(mode?: 'add' | 'edit', day?: Weekday): string {
  if (!mode) return '';
  return mode === 'add' ? `Add Time Window for ${day}` : `Edit Time Window for ${day}`;
}

function getTimeSlotWindowValidationError(
  startTime: string,
  endTime: string,
  validateTimeSlotWindow?: (
    startTime: string,
    endTime: string
  ) => { isValid: boolean; error?: string }
): string {
  if (startTime && endTime && validateTimeSlotWindow) {
    const validation = validateTimeSlotWindow(startTime, endTime);
    return validation.isValid ? '' : validation.error || '';
  }
  return '';
}

function prepareSaveData(
  startTime: string,
  endTime: string,
  powerSetpoint: string,
  validateTimeSlotWindow?: (
    startTime: string,
    endTime: string
  ) => { isValid: boolean; error?: string }
): { startTime: string; endTime: string; powerSetpoint: number } | null {
  if (!startTime || !endTime || !powerSetpoint) return null;

  const powerValue = parseFloat(powerSetpoint);
  if (isNaN(powerValue)) return null;

  if (validateTimeSlotWindow) {
    const validation = validateTimeSlotWindow(startTime, endTime);
    if (!validation.isValid) {
      return null;
    }
  }

  return {
    startTime,
    endTime,
    powerSetpoint: powerValue,
  };
}

function resetFormState(setFormState: Dispatch<SetStateAction<FormState>>): void {
  setFormState({
    startTime: '',
    endTime: '',
    powerSetpoint: '',
  });
}
